import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

const points = [
  'Foods that heal you & foods to avoid',
  'Personalized supplement stack with dosages',
  'In-home & gym workouts tailored to your body',
  'Stretching & mobility routines',
];

const EvaluationReady = () => {
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionStorage.getItem('wellth_latest_report')) nav('/intake');
  }, [nav]);

  const cta = user ? '/results' : '/signup';

  return (
    <div className="min-h-[85vh] flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 -left-40 w-[28rem] h-[28rem] rounded-full bg-[#7E9979]/15 blur-3xl" />
        <div className="absolute -bottom-32 -right-40 w-[28rem] h-[28rem] rounded-full bg-[#C05746]/10 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        className="max-w-2xl w-full text-center"
        data-testid="evaluation-ready-container"
      >
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6, type: 'spring', stiffness: 120 }}
          className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white border border-[#E5E1D8] mb-8 shadow-sm"
        >
          <Sparkles className="w-8 h-8 text-[#4A7C59]" strokeWidth={1.5} />
        </motion.div>

        <span className="text-xs tracking-[0.3em] uppercase font-semibold text-[#7E9979]">Complete</span>
        <h1 className="mt-4 font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#1A2421] font-medium">
          Your Health Evaluation<br />is Ready!
        </h1>
        <p className="mt-6 text-lg text-[#4A5A53] leading-relaxed font-light max-w-lg mx-auto">
          {user
            ? "Take a look at your personalized plan below."
            : "Create your free account to unlock your full personalized plan and start tracking your journey."}
        </p>

        <ul className="mt-10 space-y-3 text-left max-w-md mx-auto">
          {points.map((p, i) => (
            <motion.li
              key={p}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + i * 0.1 }}
              className="flex items-center gap-3 text-[#1A2421]"
            >
              <span className="w-6 h-6 rounded-full bg-[#4A7C59] flex items-center justify-center flex-shrink-0">
                <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
              </span>
              <span className="font-light">{p}</span>
            </motion.li>
          ))}
        </ul>

        <div className="mt-12">
          <Link to={cta}>
            <Button
              className="rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white px-10 py-7 text-base h-auto gap-2 group"
              data-testid="evaluation-ready-cta"
            >
              {user ? 'Show me my plan' : 'Create account & reveal my plan'}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.5} />
            </Button>
          </Link>
          {!user && (
            <p className="mt-4 text-xs tracking-[0.15em] uppercase font-semibold text-[#4A5A53]">
              Takes 30 seconds &middot; No credit card
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default EvaluationReady;
